/** Cross-cutting constants. Anything a magic number would otherwise be. */

export const APP_NAME = 'Pratishtha Bridal ERP';
export const APP_SLUG = 'erp-boilerplate';

export const DEFAULT_CURRENCY = 'INR';
export const DEFAULT_LOCALE = 'en-IN';
export const DEFAULT_TIMEZONE = 'Asia/Kolkata';
export const FISCAL_YEAR_START_MONTH = 4;

/* ------------------------------------------------------------------ */
/* Listing defaults                                                    */
/* ------------------------------------------------------------------ */

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

/**
 * Patterns both sides validate against. Duplicating a regex between the form and
 * the API is how the two eventually disagree about what a valid value is.
 */
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  /** 10-digit Indian mobile. Swap for your locale's rule if that is not yours. */
  PHONE: /^[6-9]\d{9}$/,
  PINCODE: /^[1-9]\d{5}$/,
  SLUG: /^[a-z0-9]+(?:_[a-z0-9]+)*$/,
} as const;

/**
 * Password policy, stated once. The API enforces it on every write and the web
 * strength meter reads the same list, so the meter can never promise something
 * the server will reject.
 */
export const PASSWORD_RULES = [
  { label: '8 characters or more', test: (value: string) => value.length >= 8 },
  { label: 'An uppercase letter', test: (value: string) => /[A-Z]/.test(value) },
  { label: 'A lowercase letter', test: (value: string) => /[a-z]/.test(value) },
  { label: 'A number', test: (value: string) => /\d/.test(value) },
] as const;

export const meetsPasswordPolicy = (value: string): boolean =>
  PASSWORD_RULES.every((rule) => rule.test(value));

/* ------------------------------------------------------------------ */
/* Document numbering                                                  */
/* ------------------------------------------------------------------ */

/**
 * Prefixes for human-readable document numbers, allocated atomically per
 * `(prefix, fiscalYear)` by the API's numbering service. Empty until your project
 * has its first numbered document — the mechanism is here, the vocabulary is not.
 */
export const DOC_PREFIX = {
  CUSTOMER: 'CUS',
  LEAD: 'LEAD',
  APPOINTMENT: 'APPT',
  /** Products use the fiscal numbering series: PRD/2026-27/0001. */
  PRODUCT: 'PRD',
  /** Sales documents, each its own fiscal series. */
  QUOTATION: 'QUO',
  SALE: 'SAL',
  INVOICE: 'INV',
  /** Payment receipts: REC/2026-27/0001. */
  RECEIPT: 'REC',
  /** Rental bookings: REN/2026-27/0001. */
  RENTAL_BOOKING: 'REN',
  /** Rental agreements: AGR/2026-27/0001. */
  RENTAL_AGREEMENT: 'AGR',
  /** Rental dispatch notes: DSP/2026-27/0001. */
  RENTAL_DISPATCH: 'DSP',
  /** Rental returns: RET/2026-27/0001. */
  RENTAL_RETURN: 'RET',
  /** Buyback / purchase-back documents: BUY/2026-27/0001. */
  BUYBACK: 'BUY',
  /**
   * Vendors: VEN/2026-27/0001 (Phase 11).
   *
   * The fiscal series, like `CUSTOMER`, rather than the flat perpetual series
   * `ProductItem` uses. A vendor is a party the business deals with, and it is
   * numbered the way the other party — the customer — already is.
   */
  VENDOR: 'VEN',
  /** Purchase documents: PUR/2026-27/0001. */
  PURCHASE: 'PUR',
  /** Vendor payments: VPY/2026-27/0001. Deliberately not the REC receipt series. */
  VENDOR_PAYMENT: 'VPY',
  /**
   * Purchase returns: PRN/2026-27/0001 (Phase 12).
   *
   * Not RET, which the rental return already owns. A goods-return note to a
   * supplier and a garment coming back from a customer are opposite events, and
   * sharing a series would make a number ambiguous the moment it is read aloud.
   */
  PURCHASE_RETURN: 'PRN',
  /** Vendor credits raised by a return an invoice could not absorb: VCR/2026-27/0001. */
  VENDOR_CREDIT: 'VCR',
  /**
   * Accounting journal entries: JE/2026-27/0001 (Phase 8).
   *
   * Allocated when a journal is POSTED, never when a draft is created. An
   * abandoned draft must not burn a number out of a fiscal series an auditor
   * expects to run without gaps.
   */
  JOURNAL: 'JE',
} as const satisfies Record<string, string>;

/**
 * Prefixes for *flat, perpetual* codes — a running sequence that does not reset per
 * fiscal year and carries no year segment (e.g. `BRD-000001`, `CAT-0001`). Minted
 * by the API's `generateSequentialCode`, which shares the same atomic counter as
 * the fiscal series but formats as `<prefix>-<padded>`.
 */
export const SEQ_PREFIX = {
  /** Physical inventory items: BRD-000001. Deliberately not derived from a product. */
  PRODUCT_ITEM: 'BRD',
  PRODUCT_CATEGORY: 'CAT',
  INVENTORY_LOCATION: 'LOC',
} as const satisfies Record<string, string>;
